import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { PaginatedResponse,PreordersServiceSort, PreordersTableRow } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { preordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * Search preorders request parameters.
 */
export interface SearchPreordersParams {
	/** Free-text search query to filter preorders */
	query?: string;
	/** Maximum number of results per page */
	limit?: number;
	/** Zero-indexed page number for pagination */
	page?: number;
	/** Sort field and direction for the results */
	sortBy?: PreordersServiceSort;
	/** Facet fields to include in the response for filter counts */
	facets?: string[];
	/** Filter criteria to narrow the search results */
	filters?: {
		/** Tab filter values to restrict results by preorder stage */
		tabs?: string[];
		/** When true, only return preorders requiring action */
		actionRequired?: boolean;
		/** Status value(s) to filter by */
		status?: string | string[];
		/** Vehicle name value(s) to filter by */
		name?: string | string[];
		/** Registration state abbreviation to filter by */
		registrationState?: string;
		/** Order date range filter with Unix ms timestamps */
		orderDateRange?: { gte: number; lte?: number };
		/** Preorder amount range filter in dollars */
		preorderAmountRange?: { min?: number; max?: number };
		/** Estimated retail price range filter in dollars */
		estRetailPriceRange?: { min?: number; max?: number };
	};
	/** IANA timezone string used for date-range calculations */
	timezone?: string;
}

/**
 * API function to search preorders with optional filters and pagination.
 * @param servicesProviderId - Required services provider ID
 * @param params - Optional search parameters
 */
export const searchPreorders = async (
	servicesProviderId: string,
	params: SearchPreordersParams = {},
): Promise<PaginatedResponse<PreordersTableRow>> => {
	const timezone = params.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
	const response = await apiClient.post<PaginatedResponse<PreordersTableRow>>(
		preordersEndpoints.search(servicesProviderId),
		{ ...params, timezone },
	);
	return response.data;
};

/**
 * Hook for searching preorders with optional filters and pagination.
 * Uses the current services provider context and caches results indefinitely (until invalidation).
 * @param params - Search and filter parameters
 */
export const useSearchPreorders = (
	params: SearchPreordersParams = {},
): UseQueryResult<PaginatedResponse<PreordersTableRow>, Error> => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.preorders.list(servicesProviderId, params).queryKey,
		queryFn: () => searchPreorders(servicesProviderId, params),
		enabled: Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
